import cx from 'classnames';
import { Link } from 'react-router-dom';
import Typography from '@material-ui/core/Typography';
import React, { useEffect } from 'react';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { makeStyles } from '@material-ui/styles';
import Button from 'components/common/Button';
import CheckIcon from '@material-ui/icons/Check';
import HourglassEmptyIcon from '@material-ui/icons/HourglassEmpty';
import CreateIcon from '@material-ui/icons/Create';
import EditIcon from '@material-ui/icons/Edit';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import { toUserTimezone } from 'utils';

const useStyles = makeStyles((theme) => ({
  root: {
    'position': 'relative',
    '&:after': {
      content: '""',
      width: 3,
      backgroundColor: '#A7D784',
      position: 'absolute',
      left: theme.spacing(7),
      bottom: -theme.spacing(16),
      height: theme.spacing(16),
    },
    '&:last-child': {
      '&:after': {
        display: 'none',
      },
    },
  },
  card: {
    boxShadow: '0px 10px 20px rgba(180, 186, 200, 0.3)',
    marginBottom: theme.spacing(10),
    borderRadius: 12,
  },
  cardContent: {
    'padding': theme.spacing(4),
    'paddingTop': theme.spacing(6),
    '& .card_header': {
      'display': 'flex',
      'alignItems': 'center',
      'marginBottom': theme.spacing(4),
      '& a': {
        cursor: 'pointer',
      },
    },
    '& .success': {
      color: '#A7D784',
    },
  },
  separator: {
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
  },
  cardActions: {
    boxShadow: 'inset 0px 1px 0px #D2D7E3',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  btnIcon: {
    marginRight: theme.spacing(1),
  },
  taskItem: {
    'marginBottom': theme.spacing(2.5),
    '& .grid': {
      display: 'flex',
      alignItems: 'center',
      marginTop: 4,
    },
    '& .flex': {
      flex: 1,
    },
    '&:last-child': {
      marginBottom: 0,
    },
    '& a': {
      color: 'inherit',
      fontWeight: 500,
    },
  },
  btnRounded: {
    borderRadius: 20,
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    width: '90%',
  },
  statusIcon: {
    'marginLeft': theme.spacing(4),
    'position': 'relative',
    'top': 27,
    'color': '#A7D784',
    'width': 54,
    'height': 54,
    'borderRadius': 27,
    'border': '2px solid #A7D784',
    'textAlign': 'center',
    'lineHeight': '4rem',
    'backgroundColor': 'white',
    '&.done': {
      backgroundColor: '#A7D784',
      color: 'white',
    },
  },
  scheduleActions: {
    'marginTop': theme.spacing(1),
    'display': 'flex',
    'justifyContent': 'space-between',
    '& a': {
      color: theme.palette.primary.main,
    },
    '& .cancel': {
      color: theme.palette.secondary.main,
      marginLeft: theme.spacing(1),
    },
  },
}));

const STATUS = {
  inprogress: 'In progress',
  done: 'Done',
  noshow: 'No Show'
};

const STATUS_COLOR = {
  inprogress: '#FFAD0F',
  done: '#8ED76E',
  new: '#AAB5CE',
  noshow: '#DC143C'
};

const TaskItem = ({
  task,
  userId,
  studentSub,
  user,
  student,
  onSchedule,
  onReschedule,
  onCancelMeeting,
  readOnly,
  disableView,
  exerciseRef,
}) => {
  const classes = useStyles();
  const { id, status, due_date, exercise, response } = task;
  const { name, type, module } = exercise;
  const isDone = status === 'done';
  const noShow = status === 'noshow'

  useEffect(() => {
    status === 'done' &&
      exerciseRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
  }, []);

  if (type === 'meeting') {
    if (isDone || noShow) {
      return (
        <div className={classes.taskItem} ref={exerciseRef}>
          <div className="grid">
            <Typography
              variant="caption"
              color="textSecondary"
              className="flex"
            >
              {module.name}
            </Typography>
            {due_date && (
              <Typography variant="caption" color="textSecondary">
                Scheduled{' '}
                {toUserTimezone(due_date, user.timezone).format(
                  'MM-DD-YYYY hh:mm A z',
                )}
              </Typography>
            )}
          </div>
          <div className="grid">
            <Typography color="textPrimary" className="flex">
              {name}
            </Typography>
            <Typography variant="subtitle2" color="primary">
              {response?.canceled ? (
                <span style={{ color: 'red' }}>Canceled</span>
              ) : (
                <Typography
                  color="textSecondary"
                  variant="caption"
                  style={{ color: STATUS_COLOR[status] || STATUS_COLOR.new }}
                >
                  {STATUS[status]}
                </Typography>
              )}
            </Typography>
          </div>
        </div>
      );
    }
    return (
      <div className={classes.taskItem} ref={exerciseRef}>
        <div className="grid">
          <Typography variant="caption" color="textSecondary" className="flex">
            {module.name}
          </Typography>
          {due_date ? (
            <Typography variant="caption" color="textSecondary">
              Scheduled{' '}
              {toUserTimezone(due_date, user.timezone).format(
                'MM-DD-YYYY hh:mm A z',
              )}
            </Typography>
          ) : null}
        </div>
        <div className="grid">
          {due_date && !disableView ? (
            <Typography color="primary" className="flex">
              <a
                target="_blank"
                href={
                  student.group
                    ? `/meeting/group/${student.group.group_id}`
                    : `/meeting/${studentSub}`
                }
              >
                {name}
              </a>
            </Typography>
          ) : (
            <Typography color="textPrimary" className="flex">
              {name}
            </Typography>
          )}
          {!readOnly && (
            <Typography variant="subtitle2" color="primary">
              {due_date && onReschedule ? (
                <div className={classes.scheduleActions}>
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      onReschedule(task);
                    }}
                  >
                    Reschedule Session
                  </a>
                  <a
                    className="cancel"
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      onCancelMeeting(task);
                    }}
                  >
                    Cancel Session
                  </a>
                </div>
              ) : (
                onSchedule && (
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      onSchedule(task);
                    }}
                  >
                    Schedule Meeting
                  </a>
                )
              )}
            </Typography>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={classes.taskItem} ref={exerciseRef}>
      <div className="grid">
        <Typography variant="caption" color="textSecondary" className="flex">
          {module.name}
        </Typography>
        {due_date && (
          <Typography variant="caption" color="textSecondary">
            Due {moment.utc(due_date).format('LL')} |&nbsp;
          </Typography>
        )}
        <Typography
          color="textSecondary"
          variant="caption"
          style={{ color: STATUS_COLOR[status] || STATUS_COLOR.new }}
        >
          {STATUS[status] || 'Not Started'}
        </Typography>
      </div>
      <div className="grid">
        <Typography color="textPrimary" className="flex">
          {name}
        </Typography>
        {!disableView && (
          <Typography variant="subtitle2" color="primary">
            <Link to={`/students/${userId}/exercises/${id}/view`}>View</Link>
          </Typography>
        )}
      </div>
    </div>
  );
};

const Milestone = ({
  student,
  milestone,
  onClick,
  userId,
  onSchedule,
  onReschedule,
  onCancelMeeting,
  readOnly,
  disableView,
}) => {
  const classes = useStyles();
  const user = useSelector((s) => s.user.user);
  const tasks = milestone.exercises;
  const isDone = milestone.status === 'done';
  const inProgress = milestone.status === 'inprogress';
  const { user_cognito_sub } = student;

  return (
    <div className={classes.root}>
      <div className={cx(classes.statusIcon, { done: isDone })}>
        {isDone ? (
          <CheckIcon className="done" color="inherit" />
        ) : inProgress ? (
          <CreateIcon color="inherit" />
        ) : (
          <HourglassEmptyIcon color="inherit" />
        )}
      </div>
      <Card className={classes.card}>
        <CardContent className={classes.cardContent}>
          <div className="card_header">
            <Typography variant="subtitle1">{milestone.name}</Typography>
            {milestone.due_date && (
              <Typography variant="body2" color="textSecondary">
                <span className={classes.separator}>|</span>{' '}
                {moment.utc(milestone.due_date).format('MMMM DD, YYYY')}
              </Typography>
            )}
            {!readOnly && (
              <div style={{ marginLeft: 'auto' }}>
                <Button
                  color="primary"
                  startIcon={<EditIcon fontSize="small" />}
                  onClick={() => onClick(milestone)}
                >
                  Edit
                </Button>
              </div>
            )}
          </div>
          <div className="card_body">
            <div>
              {tasks.map((i, idx) => {
                const exerciseRef = React.createRef();
                return (
                  <TaskItem
                    student={student}
                    exerciseRef={exerciseRef}
                    task={i}
                    key={i.id}
                    readOnly={readOnly}
                    disableView={disableView}
                    userId={userId}
                    studentSub={user_cognito_sub}
                    user={user}
                    onSchedule={onSchedule}
                    onReschedule={onReschedule}
                    onCancelMeeting={onCancelMeeting}
                  />
                );
              })}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Milestone;
